import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChartLineUp, Trophy, Quotes } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const FEATURES = [
  { icon: ChartLineUp, title: "AI Görünürlük Skoru", desc: "ChatGPT, Perplexity ve Gemini sizi ne sıklıkla öneriyor — 0–100 arası tek bir skor." },
  { icon: Trophy, title: "Rakip Karşılaştırma", desc: "Sizin markanız mı yoksa rakipleriniz mi öne çıkıyor? Net tablo." },
  { icon: Quotes, title: "AI hangi kaynaklara güveniyor?", desc: "Yapay zekânın referans aldığı kaynakları görün, oralarda yer alın." },
];

export default function Login() {
  const { user, loading, login, loginWithGoogleCredential } = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);
  const [googleError, setGoogleError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (loading || user) return;

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setGoogleError("Google Client ID eksik.");
      return;
    }

    const renderGoogleButton = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            if (!response?.credential) throw new Error("Google credential missing");
            await loginWithGoogleCredential(response.credential);
          } catch (err) {
            console.error(err);
            setGoogleError("Google girişi başarısız oldu. Lütfen tekrar deneyin.");
          }
        },
      });

      googleButtonRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        width: 320,
        text: "continue_with",
        shape: "rectangular",
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return;
    }

    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.addEventListener("load", renderGoogleButton, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    script.onerror = () => setGoogleError("Google giriş scripti yüklenemedi.");
    document.body.appendChild(script);
  }, [loading, user, loginWithGoogleCredential]);

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      <div className="lg:col-span-2 flex flex-col justify-between p-8 md:p-12">
        <Logo />

        <div className="max-w-sm py-12">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-brand mb-5">AEO / GEO Tracker</p>

          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            AI sizi öneriyor mu, yoksa sadece rakiplerinizi mi?
          </h1>

          <p className="text-muted-foreground mt-5 text-base leading-relaxed">
            Potansiyel müşteriler yapay zekâya "en iyi ..." diye sorduğunda çıkıyor musunuz?
            Ücretsiz ilk raporunuzla öğrenin.
          </p>

          <div className="mt-8 min-h-[44px] flex items-center" ref={googleButtonRef} />

          {googleError && (
            <p className="text-xs text-red-500 mt-3">{googleError}</p>
          )}

          <button
            type="button"
            onClick={login}
            className="mt-4 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            Giriş kodu ile devam et
          </button>

          <p className="text-xs text-muted-foreground mt-4">
            Devam ederek hizmet şartlarını kabul etmiş olursun. İlk rapor ücretsizdir.
          </p>
        </div>

        <p className="font-mono text-xs text-muted-foreground">© 2026 AI Marka Radar</p>
      </div>

      <div className="hidden lg:flex lg:col-span-3 relative bg-brand overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1782397132123-0166b524d6bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="AI Marka Radar"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />

        <div className="relative z-10 flex flex-col justify-end p-12 w-full">
          <div className="space-y-6 max-w-lg">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex gap-4 bg-white/10 backdrop-blur-xl border border-white/15 p-5">
                <f.icon size={28} weight="bold" className="text-white shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading font-semibold text-white text-lg">{f.title}</h3>
                  <p className="text-white/70 text-sm mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
